// Helpers for the Order Edit page: the week's invoice split into its
// deliveries, the lines of it the page sends back to be changed, and the
// meals the week's menu offers to add.
import {isAvailableOn} from 'fed-lib/src/menu/availability';

// A line a customer ordered (a meal of one account) can be cancelled. Delivery
// charges cannot: they are priced from the meals and go when the meals do.
export function isOrderedLine(item) {
  return !!item.uid && item.quantity > 0 && !`${item.order_type}`.startsWith('shipping');
}

// What tells one ordered line of an invoice from another, and finds its entry
// on the order: the account, the kind of order, the meal's date and time of
// day, the meal, and for a flex meal its stream.
export function lineKey(item) {
  return [item.uid, item.order_type, item.date, item.tod || '', item.meal_id || '', item.stream || ''].join('|');
}

// The part of a line the API needs to find it on the order, with how many of it to cancel
export function toCancelLine(item, quantity) {
  const {uid, order_type, date, tod = null, meal_id = null, stream = null, description} = item;
  return {uid, order_type, date, tod, meal_id, stream, quantity, description};
}

// An ordered line as the API adds more of it: what finds its entry on the order, and how many more
export function toAddLine(item, quantity) {
  return {
    ...toCancelLine(item, quantity),
    is_substitution: !!item.is_substitution,
    is_allergy_substitution: !!item.is_allergy_substitution,
    chefs_choice: !!item.chefs_choice,
    only_once: !!item.only_once,
  };
}

/**
 * The invoice as its deliveries, in date order: one per shipment (a delivery
 * date and an address), each with the lines ordered for it and its delivery
 * charges. `deliveries` gives each delivery date's edit deadline.
 */
export function groupInvoiceByDelivery(invoice, deliveries = []) {
  if (!invoice) {
    return [];
  }
  const deadlineOf = Object.fromEntries(deliveries.map(d => [d.deliveryDate, d]));
  const groups = (invoice.shipments || []).map(shipment => ({
    shipment: shipment.shipment,
    deliveryDate: shipment.shipDate,
    address: shipment.address,
    ...deadlineOf[shipment.shipDate],
    lines: [],
    charges: [],
  }));
  const groupOf = Object.fromEntries(groups.map(group => [group.shipment, group]));

  (invoice.invoiceItems || []).forEach(item => {
    const group = groupOf[item.shipment];
    if (!group) {
      return;
    }
    if (isOrderedLine(item)) {
      group.lines.push({...item, key: lineKey(item)});
    } else {
      group.charges.push(item);
    }
  });
  return groups
    .filter(group => group.lines.length > 0 || group.charges.length > 0)
    .sort((a, b) => `${a.deliveryDate}`.localeCompare(`${b.deliveryDate}`) || a.shipment - b.shipment);
}

// Invoice lines that belong to no delivery (there should be none): shown so that nothing on the invoice is hidden
export function linesWithoutDelivery(invoice) {
  if (!invoice) {
    return [];
  }
  const shipments = new Set((invoice.shipments || []).map(s => s.shipment));
  return (invoice.invoiceItems || []).filter(item => !shipments.has(item.shipment));
}

// The selected lines with the quantity to cancel of each. `cancelQuantities` is {line key: quantity}.
export function selectedCancelLines(deliveryGroups, cancelQuantities) {
  return deliveryGroups
    .flatMap(group => group.lines)
    .filter(line => cancelQuantities[line.key] > 0)
    .map(line => ({line, quantity: Math.min(cancelQuantities[line.key], line.quantity)}));
}

// Does the selection take some, but not all, of the meals out of a box? Such a
// box is saved again with the meals left, and its size is then a choice. A box
// belongs to one account and one delivery date (a box line's `date`).
export function leavesPartOfABox(deliveryGroups, cancelQuantities) {
  const boxes = {};
  deliveryGroups
    .flatMap(group => group.lines)
    .filter(line => line.order_type === 'box')
    .forEach(line => {
      const boxKey = `${line.uid}|${line.date}`;
      const box = boxes[boxKey] = boxes[boxKey] || {ordered: 0, cancelled: 0};
      box.ordered += line.quantity;
      box.cancelled += Math.min(cancelQuantities[line.key] || 0, line.quantity);
    });
  return Object.values(boxes).some(box => box.cancelled > 0 && box.cancelled < box.ordered);
}

// ---------------------------------------------------------------------------
// The meals that can be added or put in another's place, from the week's menu
// as the store's fetchMenu answers it:
//   {<stream>: {derived?, menu: [{date, meals: [{id, name, tod, date, stream, deliveryDate}]},
//                                {date: 'any', products: [{id, name, tod, category, price, availableFrom, availableTo}]}]}}
// ---------------------------------------------------------------------------

function mealsOfStreamMenu(streamMenu) {
  return ((streamMenu && streamMenu.menu) || [])
    .filter(day => day.date !== 'any')
    .flatMap(day => day.meals || []);
}

// by day, then breakfast, lunch, dinner, then name
const TOD_ORDER = {breakfast: 0, lunch: 1, dinner: 2};
const sortKey = meal => `${meal.date}-${TOD_ORDER[meal.tod] ?? 3}-${meal.name}`;
const byDateAndTod = (a, b) => sortKey(a).localeCompare(sortKey(b));

// The meals of a profile's own plan: one per day and time of day
export function planMealOptions(streamMenu) {
  return mealsOfStreamMenu(streamMenu).slice().sort(byDateAndTod);
}

// The streams whose meals can stand in for a plan meal or go in a box: the
// streams with a menu of their own (flex and the like are made of the others)
function streamsWithOwnMenu(menus) {
  return Object.keys(menus || {}).filter(stream => !menus[stream].derived && stream !== 'flex');
}

// The delivery a meal date ships with, as the menu has it
export function deliveryDateOfMealDate(menus, mealDate) {
  const meal = streamsWithOwnMenu(menus)
    .flatMap(stream => mealsOfStreamMenu(menus[stream]))
    .find(m => m.date === mealDate && m.deliveryDate);
  return meal ? meal.deliveryDate : null;
}

// The days of the week that have meals, and its deliveries
export function mealDatesOf(menus) {
  const dates = streamsWithOwnMenu(menus).flatMap(stream => mealsOfStreamMenu(menus[stream])).map(m => m.date);
  return [...new Set(dates)].sort();
}

export function deliveryDatesOf(menus) {
  const dates = streamsWithOwnMenu(menus)
    .flatMap(stream => mealsOfStreamMenu(menus[stream]))
    .map(m => m.deliveryDate)
    .filter(Boolean);
  return [...new Set(dates)].sort();
}

// The meals of the other streams that ship with a delivery: what a plan meal
// of that delivery can be substituted by
export function substituteOptions(menus, ownStream, deliveryDate) {
  return streamsWithOwnMenu(menus)
    .filter(stream => stream !== ownStream)
    .flatMap(stream => mealsOfStreamMenu(menus[stream]))
    .filter(meal => meal.deliveryDate === deliveryDate)
    .sort(byDateAndTod);
}

// The chef's choice products (snacks and extras) on offer for a delivery:
// those whose availability window admits it (fed-lib menu/availability.js —
// a Monday office delivery follows its Sunday)
export function extraOptions(menus, deliveryDate) {
  const anyDay = ((menus && menus.full && menus.full.menu) || []).find(day => day.date === 'any');
  return ((anyDay && anyDay.products) || [])
    .filter(product => !deliveryDate || isAvailableOn(product, deliveryDate))
    .slice()
    .sort((a, b) => `${a.name}`.localeCompare(`${b.name}`));
}

// Everything that can go in the box of a delivery: each stream meal that
// ships with it (once, though several streams may serve it) and the extras
export function boxMealOptions(menus, deliveryDate) {
  const streamMeals = {};
  streamsWithOwnMenu(menus)
    .flatMap(stream => mealsOfStreamMenu(menus[stream]))
    .filter(meal => meal.deliveryDate === deliveryDate)
    .forEach(meal => {
      streamMeals[meal.id] = streamMeals[meal.id] || meal;
    });
  const extras = extraOptions(menus, deliveryDate).map(product => ({...product, chefs_choice: true}));
  return [...Object.values(streamMeals).sort(byDateAndTod), ...extras];
}
